import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getDocs, collection, doc, updateDoc, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../firebase';
import { formatDistanceToNow, differenceInDays, differenceInHours } from 'date-fns';
import { Popover } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import JobEditModal from '../job-summary/JobEditModal';
import JobWorkspaceModal from '../job-summary/JobWorkspaceModal';
import { Job, JobStatus } from '../job-summary/Interfaces';
import { Timestamp } from 'firebase/firestore';
import { KTSVG } from '../../../_metronic/helpers';
import CalendarModal from '../../modals/CalendarModal';
import JobStatusService from '../../utils/jobStatusService';
import { useEmailPanel, PanelEmailData } from '../mail/contexts/EmailPanelContext';
import { FeedEmailData } from '../mail/components/EmailFeedContent';
import CustomerListToolbar from './components/CustomerListToolbar';
import CustomerCard from './components/CustomerCard';
import TerminalCustomerCard from './components/TerminalCustomerCard';
import JobCompactList from './components/JobCompactList';
import { useTerminalTheme } from '../../hooks/useTerminalTheme';
import {
  CustomerWithJobs,
  JobWithCustomer,
  PopoverState,
  SortField,
  SortOrder,
  ViewMode,
  StatusFilter,
  MAX_EMAILS_PER_JOB_IN_CARD,
} from './utils/customerListTypes';

const ActiveCustomerList: React.FC = () => {
  const isTerminal = useTerminalTheme();

  const [customers, setCustomers] = useState<CustomerWithJobs[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('dueDate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => {
    // Load from localStorage with default to show Open, Pending, and Active jobs
    const saved = localStorage.getItem('activeCustomerList_statusFilter');
    return saved ? JSON.parse(saved) : ['Active', 'Open', 'Pending'];
  });
  const [popover, setPopover] = useState<PopoverState>({
    anchorEl: null,
    jobId: null,
    currentDate: null,
    tempDate: null,
  });
  const [updating, setUpdating] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [jobToEdit, setJobToEdit] = useState<Job | null>(null);
  const [updatingLastContactedFor, setUpdatingLastContactedFor] = useState<string | null>(null);
  const [lastContactedError, setLastContactedError] = useState<string | null>(null);
  const [isCalendarModalVisible, setIsCalendarModalVisible] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState<string | null>(null);
  const [selectedJobForStatus, setSelectedJobForStatus] = useState<Job | null>(null);
  
  // Ref to track if component has finished initial load
  const hasInitiallyLoaded = useRef(false);

  // Save status filter to localStorage whenever it changes
  const updateStatusFilter = (newFilter: StatusFilter) => {
    setStatusFilter(newFilter);
    localStorage.setItem('activeCustomerList_statusFilter', JSON.stringify(newFilter));
  };

  // Get user ID for activity tracking - match UserDashboard logic
  const getUserId = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      return parsedUser?.uid || parsedUser?.id || 'default-user';
    }
    return 'default-user';
  };
  const userId = getUserId();
  
  // Timer system removed. These stubs preserve the prop-shapes downstream
  // components (CustomerCard / JobRow / JobStatusDropdown / JobCompactList)
  // still expect, without pulling back in the timer hook. Safe because every
  // active-check condition resolves to false.
  const currentSessionMinutes = 0;
  const formatTime = (_m: number): string => '0h';

  // Email panel hook - for displaying recent emails per job
  const { openEmailPanel, preLoadedEmails, isLoadingInitialEmails, loadEmailsForDateRange, loadEmailsByIds } = useEmailPanel();

  // Per-customer "hydration" (one-time action, persisted locally so it auto-runs on reload)
  const CUSTOMER_HYDRATION_STORAGE_KEY = 'activeCustomerList.customerEmailHydration.v1';
  const [hydratedCustomers, setHydratedCustomers] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(CUSTOMER_HYDRATION_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });
  const hydratedCustomersRef = useRef<Record<string, boolean>>(hydratedCustomers);
  useEffect(() => {
    hydratedCustomersRef.current = hydratedCustomers;
    try {
      localStorage.setItem(CUSTOMER_HYDRATION_STORAGE_KEY, JSON.stringify(hydratedCustomers));
    } catch {}
  }, [hydratedCustomers]);

  const [hydratingCustomer, setHydratingCustomer] = useState<string | null>(null);
  const [customerHydrationErrors, setCustomerHydrationErrors] = useState<Record<string, string>>({});
  const hydratedThisSessionRef = useRef<Record<string, boolean>>({});
  // Counter to force auto-hydration effect to re-run after each customer completes
  const [hydrationCycleCounter, setHydrationCycleCounter] = useState(0);

  // Build customer email index: email address → customerId
  const customerEmailIndex = useMemo(() => {
    const index: Record<string, string> = {};
    customers.forEach((customer) => {
      if (customer.email) {
        index[customer.email.toLowerCase().trim()] = customer.id;
      }
    });
    return index;
  }, [customers]);

  // Helper: extract recipient emails from an email (for outbound matching)
  const extractRecipientEmails = (email: FeedEmailData): string[] => {
    const addresses: string[] = [];
    const addAddresses = (field: any) => {
      if (!field) return;
      if (Array.isArray(field)) {
        field.forEach((item) => {
          if (typeof item === 'string') addresses.push(item.toLowerCase().trim());
          else if (item?.address) addresses.push(item.address.toLowerCase().trim());
        });
      } else if (typeof field === 'string') {
        addresses.push(field.toLowerCase().trim());
      } else if (field?.address) {
        addresses.push(field.address.toLowerCase().trim());
      }
    };
    addAddresses(email.to);
    addAddresses(email.cc);
    addAddresses(email.bcc);
    return addresses;
  };

  // Helper: get the "true customer" for an email based on direction
  const getTrueCustomerId = (email: FeedEmailData): string | null => {
    const isOutbound = (email as any).direction === 'out' || email.folder === 'sent' || email.emailType === 'sent';
    
    if (isOutbound) {
      // For sent emails: the customer is the RECIPIENT
      const recipientEmails = extractRecipientEmails(email);
      for (const addr of recipientEmails) {
        const matchedCid = customerEmailIndex[addr];
        if (matchedCid) return matchedCid;
      }
    } else {
      // For received emails: the customer is the SENDER
      // Check existing customerId first
      if (email.customerId && typeof email.customerId === 'string') return email.customerId;
      if (Array.isArray(email.customerId) && email.customerId.length > 0) return email.customerId[0];
      
      // Otherwise match sender
      const senderAddr = email.from?.[0]?.address?.toLowerCase().trim();
      if (senderAddr) {
        const matchedCid = customerEmailIndex[senderAddr];
        if (matchedCid) return matchedCid;
      }
    }
    return null;
  };

  // Build job → emails mapping
  // For each job, find emails that are:
  // 1. Explicitly linked to the job (linkedJobId)
  // 2. OR from/to the job's customer (and not linked to another job)
  const jobEmailsMap = useMemo(() => {
    const map: Record<string, FeedEmailData[]> = {};
    
    // First pass: emails with explicit linkedJobId
    const linkedEmailIds = new Set<string>();
    preLoadedEmails.forEach((email) => {
      if (email.linkedJobId) {
        if (!map[email.linkedJobId]) map[email.linkedJobId] = [];
        map[email.linkedJobId].push(email);
        linkedEmailIds.add(email.id);
      }
    });

    // Second pass: emails without linkedJobId, matched to customer's jobs
    preLoadedEmails.forEach((email) => {
      if (linkedEmailIds.has(email.id)) return; // Already linked
      if (email.dismissedFromWorkHub) return; // Dismissed
      
      const customerId = getTrueCustomerId(email);
      if (!customerId) return;

      // Find jobs for this customer
      customers.forEach((customer) => {
        if (customer.id !== customerId) return;
        customer.activeJobs.forEach((job) => {
          // Only add to jobs that don't have explicit links yet, or as "unlinked" emails
          if (!map[job.id]) map[job.id] = [];
          // Avoid duplicates
          if (!map[job.id].some((e) => e.id === email.id)) {
            map[job.id].push(email);
          }
        });
      });
    });

    // Sort each job's emails by date (newest first) and cap for card display
    Object.keys(map).forEach((jobId) => {
      map[jobId] = map[jobId]
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        .slice(0, MAX_EMAILS_PER_JOB_IN_CARD);
    });

    return map;
  }, [preLoadedEmails, customers, customerEmailIndex]);

  const hydrateCustomerEmails = useCallback(async (customer: CustomerWithJobs) => {
    const customerId = customer?.id;
    if (!customerId) return;
    if (hydratingCustomer) return; // single-flight to avoid overwhelming Firestore
    setCustomerHydrationErrors((p) => ({ ...p, [customerId]: '' }));
    setHydratingCustomer(customerId);
    try {
      // "All time" fetch for this customer into the shared email cache (preLoadedEmails)
      // The context's loadEmailsForDateRange queries by customerId == string match
      const start = new Date(0);
      const end = new Date();
      await loadEmailsForDateRange(start, end, customerId);

      // Collect additional email IDs to load via loadEmailsByIds
      const additionalEmailIds = new Set<string>();

      // 1) Emails where customerId is stored as an ARRAY containing this customer
      //    (covers legacy docs where customerId was array instead of string)
      try {
        const arrayMatchSnap = await getDocs(
          query(
            collection(db, 'emails'),
            where('customerId', 'array-contains', customerId),
            orderBy('date', 'desc'),
            limit(500)
          )
        );
        arrayMatchSnap.docs.forEach((d) => additionalEmailIds.add(d.id));
      } catch (e) {
        // Index may not exist; silently skip
        console.warn('[hydrateCustomerEmails] array-contains query skipped (index missing?):', e);
      }

      // 2) Emails where senderEmail matches the customer's email (inbound emails that may not have customerId set)
      const customerEmail = (customer.email || '').toLowerCase().trim();
      if (customerEmail) {
        try {
          const senderMatchSnap = await getDocs(
            query(
              collection(db, 'emails'),
              where('senderEmail', '==', customerEmail),
              orderBy('date', 'desc'),
              limit(500)
            )
          );
          senderMatchSnap.docs.forEach((d) => additionalEmailIds.add(d.id));
        } catch (e) {
          console.warn('[hydrateCustomerEmails] senderEmail query skipped (index missing?):', e);
        }
      }

      // 3) Also load job-linked emails via recentEmailIds (covers emails linked to job but not mapped to customerId)
      (customer.activeJobs || []).forEach((j: Job) => {
        const ids = (j as any).recentEmailIds;
        if (Array.isArray(ids)) {
          ids.forEach((id: any) => {
            const s = String(id || '').trim();
            if (s) additionalEmailIds.add(s);
          });
        }
      });

      // Load all additional emails into the shared cache
      if (additionalEmailIds.size > 0) {
        await loadEmailsByIds(Array.from(additionalEmailIds));
      }

      setHydratedCustomers((prev) => ({ ...prev, [customerId]: true }));
      hydratedThisSessionRef.current[customerId] = true;
    } catch (err: any) {
      console.error('[ActiveCustomerList] hydrateCustomerEmails error', err);
      setCustomerHydrationErrors((p) => ({ ...p, [customerId]: err?.message ? String(err.message) : 'Failed to load email history' }));
    } finally {
      setHydratingCustomer(null);
      // Increment counter to trigger the auto-hydration effect to pick up the next customer
      setHydrationCycleCounter((c) => c + 1);
    }
  }, [loadEmailsForDateRange, loadEmailsByIds]);

  // Handle email click - open in sliding panel
  const handleEmailClick = (email: FeedEmailData, e: React.MouseEvent) => {
    e.stopPropagation();
    openEmailPanel('viewEmail', email as PanelEmailData);
  };

  const sortCustomers = (customers: CustomerWithJobs[]) => {
    return [...customers].sort((a, b) => {
      if (sortField === 'name') {
        return sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      } else if (sortField === 'lastContacted') {
        const dateA = new Date(a.lastContacted).getTime();
        const dateB = new Date(b.lastContacted).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (sortField === 'status') {
        // Priority: Active > Open > Pending
        const getStatusPriority = (jobs: Job[]) => {
          if (jobs.some(job => job.status === 'Active')) return 3;
          if (jobs.some(job => job.status === 'Open')) return 2;
          return 1; // Pending
        };
        const priorityA = getStatusPriority(a.activeJobs);
        const priorityB = getStatusPriority(b.activeJobs);
        return sortOrder === 'asc' ? priorityA - priorityB : priorityB - priorityA;
      } else {
        const earliestDueDateA = Math.min(
          ...a.activeJobs
            .map(job => job.dueDate ? new Date(job.dueDate).getTime() : Infinity)
        );
        const earliestDueDateB = Math.min(
          ...b.activeJobs
            .map(job => job.dueDate ? new Date(job.dueDate).getTime() : Infinity)
        );
        return sortOrder === 'asc' 
          ? earliestDueDateA - earliestDueDateB 
          : earliestDueDateB - earliestDueDateA;
      }
    });
  };

  const filterCustomers = (customers: CustomerWithJobs[]) => {
    // If no statuses are selected, return empty list
    if (statusFilter.length === 0) {
      return [];
    }

    return customers
      .map(customer => {
        // Filter jobs to only include those matching selected statuses
        const filteredJobs = customer.activeJobs.filter(job => statusFilter.includes(job.status));
        
        return {
          ...customer,
          activeJobs: filteredJobs
        };
      })
      .filter(customer => {
      // Search filter
      const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           customer.email?.toLowerCase().includes(searchTerm.toLowerCase());
      
        // Status filter - only show customers who have at least one job matching selected statuses
        const hasMatchingJobs = customer.activeJobs.length > 0;

        return matchesSearch && hasMatchingJobs;
    });
  };

  const sortedAndFilteredCustomers = sortCustomers(filterCustomers(customers));

  // Job-centric flat list for compact list view: one row per job, sorted by current sort
  const sortedFlatJobRows = useMemo((): JobWithCustomer[] => {
    const flat: JobWithCustomer[] = sortedAndFilteredCustomers.flatMap((c) =>
      c.activeJobs.map((job) => ({ job, customer: c }))
    );
    const statusPriority = (s: string) => {
      if (s === 'Active') return 4;
      if (s === 'Overdue') return 3;
      if (s === 'Open') return 2;
      if (s === 'Pending' || s === 'Awaiting Client Response') return 1;
      return 0;
    };
    return flat.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') {
        cmp = a.customer.name.localeCompare(b.customer.name);
      } else if (sortField === 'lastContacted') {
        const tA = new Date(a.customer.lastContacted).getTime();
        const tB = new Date(b.customer.lastContacted).getTime();
        cmp = tA - tB;
      } else if (sortField === 'status') {
        cmp = statusPriority(a.job.status) - statusPriority(b.job.status);
      } else {
        const tA = a.job.dueDate ? new Date(a.job.dueDate).getTime() : Infinity;
        const tB = b.job.dueDate ? new Date(b.job.dueDate).getTime() : Infinity;
        cmp = tA - tB;
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [sortedAndFilteredCustomers, sortField, sortOrder]);

  // Auto-hydrate customers the user has previously enabled, but only for customers currently visible
  // Uses hydrationCycleCounter to reliably trigger re-runs after each customer completes
  useEffect(() => {
    if (!sortedAndFilteredCustomers || sortedAndFilteredCustomers.length === 0) return;
    if (hydratingCustomer) return;

    const next = sortedAndFilteredCustomers.find((c) => {
      const enabled = hydratedCustomersRef.current[c.id] === true;
      const alreadyHydratedThisSession = hydratedThisSessionRef.current[c.id] === true;
      return enabled && !alreadyHydratedThisSession;
    });
    if (!next) return;

    hydrateCustomerEmails(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedAndFilteredCustomers, hydratingCustomer, hydrationCycleCounter]);

  const fetchActiveCustomers = async () => {
    try {
      console.log('Starting to fetch active customers...');
      setLoading(true);
      setLastContactedError(null);

      const jobsSnapshot = await getDocs(collection(db, 'jobs'));
      const allJobs = jobsSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Job)); // Fetch all jobs, filtering will happen in the UI

      const customerIdsSet = new Set(
        allJobs.flatMap((job: Job) => job.customerId || [])
      );
      const customerIds = Array.from(customerIdsSet);

      const customersSnapshot = await getDocs(collection(db, 'customers'));
      const customersWithJobs: CustomerWithJobs[] = [];

      customersSnapshot.docs.forEach(doc => {
        if (customerIds.includes(doc.id)) {
          const customerData = doc.data();
          const customerJobs = allJobs.filter((job: Job) => 
            job.customerId && job.customerId.includes(doc.id)
          );

          if (customerJobs.length > 0) {
            customersWithJobs.push({
              id: doc.id,
              name: customerData.name,
              email: customerData.email,
              lastContacted: customerData.lastContacted || '',
              activeJobs: customerJobs
            });
          }
        }
      });

      setCustomers(customersWithJobs);
    } catch (error) {
      console.error('Error in fetchActiveCustomers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveCustomers().then(() => {
      hasInitiallyLoaded.current = true;
    });
  }, []);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Don't close if clicking inside the dropdown menu
      if (target.closest('.dropdown-menu')) {
        return;
      }
      
      if (showStatusDropdown) {
        setShowStatusDropdown(null);
        setSelectedJobForStatus(null);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showStatusDropdown]);

  const formatDate = (dateInput: any): string => {
    try {
      if (!dateInput) return 'Never';

      let dateToFormat: Date;

      if (dateInput instanceof Date) {
        dateToFormat = dateInput;
      } else if (dateInput && typeof dateInput.toDate === 'function') {
        dateToFormat = dateInput.toDate();
      } else if (typeof dateInput === 'string') {
        dateToFormat = new Date(dateInput);
      } else if (typeof dateInput === 'number') {
        dateToFormat = new Date(dateInput);
      } else {
        console.warn('formatDate received unhandled dateInput type:', dateInput);
        return 'Invalid Date Input';
      }

      if (isNaN(dateToFormat.getTime())) {
        console.warn('formatDate resulted in Invalid Date for input:', dateInput);
        return 'Invalid Date';
      }
      
      return formatDistanceToNow(dateToFormat, { addSuffix: true });
    } catch (error) {
      console.error('Error in formatDate:', error, 'Input was:', dateInput);
      return 'Date Error';
    }
  };

  const formatDueDate = (dueDate: string | undefined) => {
    if (!dueDate) return 'No due date';
    
    const due = new Date(dueDate);
    const now = new Date();
    
    const days = differenceInDays(due, now);
    const hours = differenceInHours(due, now) % 24;
    
    if (days < 0 || (days === 0 && hours < 0)) {
      return 'Overdue';
    }
    
    if (days === 0) {
      return `${hours}h`;
    }
    
    if (hours === 0) {
      return `${days}d`;
    }
    
    return `${days}d ${hours}h`;
  };



  const handleRefreshLastContacted = async (customerIdToUpdate: string) => {
    setUpdatingLastContactedFor(customerIdToUpdate);
    setLastContactedError(null);
    console.log(`[ActiveCustomerList] Refreshing last contacted for customer: ${customerIdToUpdate} (Robust Mode)`);

    try {
      const K_EMAILS_TO_FETCH = 5;

      const stringMatchQuery = query(
        collection(db, 'emails'),
        where('customerId', '==', customerIdToUpdate),
        orderBy('date', 'desc'),
        limit(K_EMAILS_TO_FETCH)
      );
      
      const arrayMatchQuery = query(
        collection(db, 'emails'),
        where('customerId', 'array-contains', customerIdToUpdate),
        orderBy('date', 'desc'),
        limit(K_EMAILS_TO_FETCH)
      );

      const [stringMatchSnapshot, arrayMatchSnapshot] = await Promise.all([
        getDocs(stringMatchQuery),
        getDocs(arrayMatchQuery)
      ]);

      const potentialDates: Date[] = [];

      console.log(`[ActiveCustomerList] String match query: snapshot empty? ${stringMatchSnapshot.empty}, size: ${stringMatchSnapshot.size}`);
      stringMatchSnapshot.docs.forEach(doc => {
        const data = doc.data();
        console.log(`[ActiveCustomerList] String query - Doc ID: ${doc.id}, Raw date:`, data.date, `(Type: ${typeof data.date})`);
        if (data.date) {
          if (data.date.toDate && typeof data.date.toDate === 'function') potentialDates.push(data.date.toDate());
          else if (data.date instanceof Date) potentialDates.push(data.date);
          else if (typeof data.date === 'string') {
            const parsed = new Date(data.date);
            if (!isNaN(parsed.getTime())) potentialDates.push(parsed);
            else console.warn(`[ActiveCustomerList] String query - Unparseable string date for doc ${doc.id}: ${data.date}`);
          }
        }
      });

      console.log(`[ActiveCustomerList] Array match query: snapshot empty? ${arrayMatchSnapshot.empty}, size: ${arrayMatchSnapshot.size}`);
      arrayMatchSnapshot.docs.forEach(doc => {
        const data = doc.data();
        console.log(`[ActiveCustomerList] Array query - Doc ID: ${doc.id}, Raw date:`, data.date, `(Type: ${typeof data.date})`);
        if (data.date) {
          if (data.date.toDate && typeof data.date.toDate === 'function') potentialDates.push(data.date.toDate());
          else if (data.date instanceof Date) potentialDates.push(data.date);
          else if (typeof data.date === 'string') {
            const parsed = new Date(data.date);
            if (!isNaN(parsed.getTime())) potentialDates.push(parsed);
            else console.warn(`[ActiveCustomerList] Array query - Unparseable string date for doc ${doc.id}: ${data.date}`);
          }
        }
      });
      
      console.log(`[ActiveCustomerList] Potential dates collected (JS Dates):`, potentialDates.map(d => d.toISOString()));

      if (potentialDates.length > 0) {
        potentialDates.sort((a, b) => b.getTime() - a.getTime());
        const mostRecentJsDate = potentialDates[0];
        console.log(`[ActiveCustomerList] Most recent JS Date found after client-side sort: ${mostRecentJsDate.toISOString()}`);

        const firestoreTimestampToUpdate = Timestamp.fromDate(mostRecentJsDate);
        console.log(`[ActiveCustomerList] Converted to Firestore Timestamp for update:`, firestoreTimestampToUpdate);

        const customerRef = doc(db, 'customers', customerIdToUpdate);
        await updateDoc(customerRef, {
          lastContacted: firestoreTimestampToUpdate 
        });
        console.log(`[ActiveCustomerList] Successfully updated lastContacted for ${customerIdToUpdate} to:`, firestoreTimestampToUpdate.toDate());
      } else {
        console.log(`[ActiveCustomerList] No emails with valid dates found for customer ${customerIdToUpdate} from either query.`);
      }

      await fetchActiveCustomers();

    } catch (err: any) {
      console.error(`[ActiveCustomerList] Error refreshing lastContacted for ${customerIdToUpdate}:`, err);
      setLastContactedError(`Failed to update for ${customerIdToUpdate}: ${err.message}`);
    } finally {
      setUpdatingLastContactedFor(null);
    }
  };

  const handleDateClick = (event: React.MouseEvent<HTMLElement>, job: Job) => {
    event.stopPropagation();
    setPopover({
      anchorEl: event.currentTarget,
      jobId: job.id,
      currentDate: job.dueDate ? new Date(job.dueDate) : new Date(),
      tempDate: job.dueDate ? new Date(job.dueDate) : new Date(),
    });
  };

  const handleJobClick = (event: React.MouseEvent<HTMLButtonElement>, job: Job) => {
    const target = event.target as HTMLElement;
    if (target.closest('.badge')) {
      return;
    }

    setSelectedJob(job);
    setShowTaskModal(true);
  };

  const handleCustomerCardClick = (job: Job) => {
    setJobToEdit(job);
    setShowEditModal(true);
  };

  const handleTempDateChange = (newDate: Date | null) => {
    setPopover(prev => ({
      ...prev,
      tempDate: newDate,
    }));
  };

  const handleSave = async () => {
    if (!popover.tempDate || !popover.jobId) return;

    try {
      setUpdating(true);
      
      const jobRef = doc(db, 'jobs', popover.jobId);
      await updateDoc(jobRef, {
        dueDate: popover.tempDate.toISOString(),
      });

      await fetchActiveCustomers();
      handlePopoverClose();
    } catch (error) {
      console.error('Error updating due date:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handlePopoverClose = () => {
    setPopover({
      anchorEl: null,
      jobId: null,
      currentDate: null,
      tempDate: null,
    });
  };

  const toggleCalendarModal = () => setIsCalendarModalVisible(prev => !prev);

  // Timer system removed — no jobs are ever in an "actively being worked"
  // runtime state. Kept as a stable function reference to preserve the
  // child-component prop contract.
  const isJobActive = (_job: Job): boolean => false;

  // Handle opening status change modal
  const handleOpenStatusModal = (job: Job, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedJobForStatus(job);
    setShowStatusDropdown(job.id); // Set the job ID to show the dropdown
  };

  // Handle status change
  const handleStatusChange = async (newStatus: JobStatus) => {
    if (!selectedJobForStatus) return;

    const currentStatus = selectedJobForStatus.status;
    const jobId = selectedJobForStatus.id;
    const jobLabel = selectedJobForStatus.jobLabel;

    // Close dropdown immediately for better UX
    setShowStatusDropdown(null);
    setSelectedJobForStatus(null);

    // Optimistic update — UI reflects the new status instantly. If the
    // customer now has no jobs that pass the status filter, the card
    // disappears; a toast confirms the change regardless so the user
    // sees their action land even when the result is filtered out.
    setCustomers(prevCustomers =>
      prevCustomers.map(customer => ({
        ...customer,
        activeJobs: customer.activeJobs.map(job =>
          job.id === jobId
            ? { ...job, status: newStatus }
            : job
        )
      }))
    );

    try {
      const result = await JobStatusService.updateJobStatus(
        jobId,
        newStatus,
        'user',
        `Manual status change from customer list: ${currentStatus} → ${newStatus}`,
        userId
      );

      if (!result.success) {
        // Revert optimistic update by refetching fresh server state.
        console.error('[ActiveCustomerList] Status change failed:', result.error);
        toast.error(`Could not update ${jobLabel}: ${result.error || 'unknown error'}`);
        await fetchActiveCustomers();
        return;
      }

      // Success: trust the optimistic update. Don't refetch — that would
      // trigger the full-page loading spinner and feel like a UI reset.
      // JobStatusService already wrote statusHistory / automation timestamps
      // on the doc; those aren't surfaced in the card so there's nothing
      // visible that needs refreshing here.
      toast.success(`${jobLabel}: ${currentStatus} → ${newStatus}`);
    } catch (error: any) {
      console.error('[ActiveCustomerList] Error changing job status:', error);
      toast.error(`Could not update ${jobLabel}: ${error?.message || 'network error'}`);
      // Revert optimistic update on unexpected errors
      await fetchActiveCustomers();
    }
  };

  if (loading && !updatingLastContactedFor) {
    return (
      <div className="d-flex justify-content-center py-10">
        <span className="spinner-border spinner-border-lg"></span>
      </div>
    );
  }

  return (
    <>
      <CustomerListToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={updateStatusFilter}
        statusFilterOpen={showStatusDropdown === 'filter'}
        onStatusFilterToggle={() => setShowStatusDropdown((prev) => (prev === 'filter' ? null : 'filter'))}
        onStatusFilterClose={() => setShowStatusDropdown(null)}
        sortField={sortField}
        sortOrder={sortOrder}
        onSortFieldChange={setSortField}
        onSortOrderChange={setSortOrder}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        customerCount={sortedAndFilteredCustomers.length}
        lastContactedError={lastContactedError}
        onRefresh={() => fetchActiveCustomers()}
        refreshing={loading}
        onCalendarOpen={toggleCalendarModal}
      />

      {/* Body */}
      <div className="card-body">
        {sortedAndFilteredCustomers.length === 0 ? (
          <div className="text-center py-10">
            {searchTerm || statusFilter.length !== 8 ? (
              // Filtered view with no results
              <>
                <KTSVG path="/media/icons/duotune/files/fil024.svg" className="svg-icon-4x opacity-50 mb-5" />
                <h4 className="fw-semibold text-gray-400 mb-3">No Customers Found</h4>
                <p className="text-gray-400 fs-6 mb-5">
                  Try adjusting your filters or search terms.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setSearchTerm('');
                    updateStatusFilter(['Active', 'Open', 'Pending', 'Awaiting Client Response', 'Overdue', 'Closed', 'Cancelled', 'Archived']);
                  }}
                >
                  Clear Filters
                </button>
              </>
            ) : (
              // No customers exist at all
              <>
                <div className="mb-5">
                  <i className="bi bi-people text-primary" style={{ fontSize: '4rem' }}></i>
                </div>
                <h4 className="fw-bold mb-3">No Active Customers</h4>
                <p className="text-muted mb-5">
                  No customers with active jobs found.<br />
                  Active customers will appear here when they have open or pending jobs.
                </p>
              </>
            )}
          </div>
        ) : (
          // Render customers based on view mode
          viewMode === 'grid' ? (
            // Terminal theme uses a bespoke log-line card (full-width stack);
            // classic theme keeps the Metronic 2-column grid.
            isTerminal ? (
              <div className="term-cust-card-stack">
                {sortedAndFilteredCustomers.map((customer) => (
                  <TerminalCustomerCard
                    key={customer.id}
                    customer={customer}
                    jobEmailsMap={jobEmailsMap}
                    updatingLastContactedFor={updatingLastContactedFor}
                    showStatusDropdown={showStatusDropdown}
                    onJobClick={handleJobClick}
                    onDateClick={handleDateClick}
                    onOpenStatusModal={handleOpenStatusModal}
                    onStatusDropdownClose={() => {
                      setShowStatusDropdown(null);
                      setSelectedJobForStatus(null);
                    }}
                    onStatusChange={handleStatusChange}
                    onEmailClick={handleEmailClick}
                    onRefreshLastContacted={() => handleRefreshLastContacted(customer.id)}
                    onViewAllJobs={() => {
                      setSelectedJob(customer.activeJobs[0]);
                      setShowTaskModal(true);
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="row g-4">
                {sortedAndFilteredCustomers.map((customer) => (
                  <CustomerCard
                    key={customer.id}
                    customer={customer}
                    jobEmailsMap={jobEmailsMap}
                    hydratedCustomers={hydratedCustomers}
                    hydratingCustomer={hydratingCustomer}
                    customerHydrationErrors={customerHydrationErrors}
                    updatingLastContactedFor={updatingLastContactedFor}
                    showStatusDropdown={showStatusDropdown}
                    currentSessionMinutes={currentSessionMinutes}
                    formatDate={formatDate}
                    formatTime={formatTime}
                    formatDueDate={formatDueDate}
                    isJobActive={isJobActive}
                    onCardClick={() => handleCustomerCardClick(customer.activeJobs[0])}
                    onHydrate={() => hydrateCustomerEmails(customer)}
                    onRefreshLastContacted={() => handleRefreshLastContacted(customer.id)}
                    onEditJob={() => {
                      setJobToEdit(customer.activeJobs[0]);
                      setShowEditModal(true);
                    }}
                    onJobClick={handleJobClick}
                    onDateClick={handleDateClick}
                    onOpenStatusModal={handleOpenStatusModal}
                    onStatusDropdownClose={() => {
                      setShowStatusDropdown(null);
                      setSelectedJobForStatus(null);
                    }}
                    onStatusChange={handleStatusChange}
                    onEmailClick={handleEmailClick}
                    onViewAllJobs={() => {
                      setSelectedJob(customer.activeJobs[0]);
                      setShowTaskModal(true);
                    }}
                  />
                ))}
              </div>
            )
          ) : (
            // List View — job-centric compact table (one row per job)
            <JobCompactList
              rows={sortedFlatJobRows}
              formatTime={formatTime}
              formatDueDate={formatDueDate}
              isJobActive={isJobActive}
              currentSessionMinutes={currentSessionMinutes}
              showStatusDropdown={showStatusDropdown}
              onJobClick={handleJobClick}
              onDateClick={handleDateClick}
              onOpenStatusModal={handleOpenStatusModal}
              onStatusDropdownClose={() => {
                setShowStatusDropdown(null);
                setSelectedJobForStatus(null);
              }}
              onStatusChange={handleStatusChange}
              onEditJob={({ job }) => {
                setJobToEdit(job);
                setShowEditModal(true);
              }}
              onOpenWorkspace={({ job }) => {
                setSelectedJob(job);
                setShowTaskModal(true);
              }}
            />
          )
        )}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Popover
            open={Boolean(popover.anchorEl)}
            anchorEl={popover.anchorEl}
            // Filter backdrop-clicks so the MUI calendar popup's
            // click-away doesn't bubble up and close this outer Popover
            // — that's what left the [save] button unclickable in terminal.
            // Escape + cancel button still close normally.
            onClose={(_event, reason) => {
              if (reason === 'backdropClick') return;
              handlePopoverClose();
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            // Scope the Paper for terminal-theme overrides. Empty string
            // in classic theme so default Material chrome is untouched.
            slotProps={isTerminal
              ? { paper: { className: 'term-due-popover-paper' } }
              : {}}
          >
            <div className={isTerminal ? 'term-due-popover' : 'p-3'}>
              <DateTimePicker
                value={popover.tempDate}
                onChange={handleTempDateChange}
                // Auto-save on calendar-popup accept so the user doesn't
                // have to fight the click-away on a separate [save] click.
                onAccept={(d) => {
                  if (d) {
                    setPopover(prev => ({ ...prev, tempDate: d }));
                    // Defer the save so tempDate propagates into state
                    // before handleSave reads it.
                    window.setTimeout(() => handleSave(), 0);
                  }
                }}
                disabled={updating}
                format="dd/MM/yyyy"
                views={['year', 'month', 'day']}
                slotProps={isTerminal ? {
                  textField: { size: 'small', variant: 'outlined' },
                  popper:       { className: 'term-due-popover-picker' },
                  desktopPaper: { className: 'term-due-popover-picker-paper' },
                  actionBar: { actions: ['cancel', 'accept'] },
                } : undefined}
              />
              {isTerminal ? (
                <div className="term-due-popover__actions">
                  <button
                    type="button"
                    className="term-due-popover__action"
                    onClick={handlePopoverClose}
                    disabled={updating}
                  >
                    <span className="term-due-popover__bracket">[</span>cancel<span className="term-due-popover__bracket">]</span>
                  </button>
                  <button
                    type="button"
                    className="term-due-popover__action term-due-popover__action--primary"
                    onClick={handleSave}
                    disabled={updating}
                  >
                    <span className="term-due-popover__bracket">[</span>
                    {updating ? 'saving…' : 'save'}
                    <span className="term-due-popover__bracket">]</span>
                  </button>
                </div>
              ) : (
                <div className='d-flex justify-content-end mt-3 gap-2'>
                  <button
                    className='btn btn-sm btn-light'
                    onClick={handlePopoverClose}
                    disabled={updating}
                  >
                    Cancel
                  </button>
                  <button
                    className='btn btn-sm btn-primary'
                    onClick={handleSave}
                    disabled={updating}
                  >
                    {updating ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </Popover>
        </LocalizationProvider>

        {selectedJob && (
          <JobWorkspaceModal
            show={showTaskModal}
            onHide={() => {
              setShowTaskModal(false);
              setSelectedJob(null);
            }}
            job={selectedJob}
          />
        )}

        {jobToEdit && (
          <JobEditModal
            show={showEditModal}
            onHide={async () => {
              setShowEditModal(false);
              setJobToEdit(null);
              await fetchActiveCustomers();
            }}
            job={jobToEdit}
          />
        )}

        {isCalendarModalVisible && (
          <CalendarModal onClose={toggleCalendarModal} />
        )}

        <style>
          {`
          .pulse-dot {
            width: 6px;
            height: 6px;
            background-color: #22c55e;
            border-radius: 50%;
            display: inline-block;
            animation: pulse 2s infinite;
          }
          
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
          
          .email-row-hover:hover {
            background-color: rgba(0, 149, 232, 0.08);
          }
          `}
        </style>
      </div>
    </>
  );
};

export default ActiveCustomerList;